import { supabase } from '@/integrations/supabase/client';

export const buyerContactService = {
  async getBuyerContactInfo(buyerId: string) {
    try {
      // Get current user (seller) ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: 'User not authenticated' };
      }

      // Use the secure RPC function that respects consent mechanism
      const { data: buyerData, error } = await (supabase as any).rpc('get_secure_buyer_profile', {
        p_buyer_id: buyerId,
        p_seller_id: user.id
      });

      if (error) {
        console.error('Error fetching buyer contact info:', error);
        return { data: null, error: error.message };
      }

      if (!buyerData || buyerData.length === 0) {
        return { data: null, error: 'Buyer not found' };
      }

      const buyer = buyerData[0];
      
      // Only return contact info if buyer has viewed properties (consent mechanism)
      if (!buyer.has_viewed_properties) {
        // Log denied access attempt
        const { default: SecurityAuditService } = await import('./securityAuditService');
        await SecurityAuditService.logSecurityEvent({
          event_type: 'buyer_contact_access_denied',
          event_details: {
            buyer_id: buyerId,
            buyer_name: buyer.full_name,
            reason: 'buyer_has_not_viewed_properties',
            accessed_via: 'contact_service'
          }
        });
        
        return { 
          data: null, 
          error: 'Contact information not available. This buyer has not viewed your properties yet.' 
        };
      }

      // Log successful contact info access
      const { default: SecurityAuditService } = await import('./securityAuditService');
      await SecurityAuditService.logSecurityEvent({
        event_type: 'buyer_contact_info_viewed',
        event_details: {
          buyer_id: buyerId,
          buyer_name: buyer.full_name,
          contact_fields_accessed: ['email', 'phone_number'],
          accessed_via: 'contact_service'
        }
      });

      return { 
        data: {
          id: buyer.id,
          user_id: buyer.user_id,
          full_name: buyer.full_name,
          company_name: buyer.company_name,
          phone_number: buyer.phone_number,
          email: buyer.email
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error in buyerContactService.getBuyerContactInfo:', error);
      return { data: null, error: 'Failed to fetch buyer contact info' };
    }
  },

  async sendEmailToBuyer(buyerId: string, subject: string, message: string) {
    try {
      // Get buyer contact info first
      const { data: buyerInfo, error: contactError } = await this.getBuyerContactInfo(buyerId);
      
      if (contactError || !buyerInfo) {
        return { success: false, error: contactError || 'Buyer not found' };
      }

      // Open email client directly
      const emailSubject = encodeURIComponent(subject);
      const emailBody = encodeURIComponent(message);
      const emailUrl = `mailto:${buyerInfo.email}?subject=${emailSubject}&body=${emailBody}`;
      
      console.log('Opening email client for:', {
        to: buyerInfo.email,
        subject,
        buyerName: buyerInfo.full_name
      });
      
      // Open the email client
      window.open(emailUrl);

      // TODO: Integrate with actual email service (Resend, SendGrid, etc.)
      return { success: true, error: null };
    } catch (error) {
      console.error('Error sending email:', error);
      return { success: false, error: 'Failed to send email' };
    }
  }
};